import { useEffect, useMemo, useState } from 'react';
import { Button, IconButton, MaterialIcons, SquaredIconButton } from '@lux/ds';
import { Route, Routes } from 'react-router-dom';
import BasicSetup from '../../BasicSetup';
import { IProductConfig } from '../../configs/Products.types';
import productsConfig from '../../configs/productsConfig';
import hostnames from '../../hostnames';
import EProduct from '../../products';
import LuxLogo from './components/LuxLogo';
import NavItem from './components/NavItem';
import { NavWrapper, Nav, StyledLinks, StyledBottomButton } from './Nav.styles';

interface INavigationProps {}

const getUserProfileFromSession = () => {
  const oidcKey = Object.keys(sessionStorage).find(key => key.includes('oidc.user'));

  if (oidcKey) {
    const userInfo = JSON.parse(sessionStorage.getItem(oidcKey) as string);
    return (userInfo.profile as Record<string, unknown>) || {};
  }

  return {};
};

const getUserRolesFromSession = () => {
  const oidcKey = Object.keys(sessionStorage).find(key => key.includes('oidc.user'));

  if (oidcKey) {
    const userInfo = JSON.parse(sessionStorage.getItem(oidcKey) as string);

    return (userInfo.profile.role as string[]) || [];
  }

  return [];
};

const Navigation: React.FC<INavigationProps> = () => {
  const [open, setOpen] = useState(false);
  const [userRoles, setUserRoles] = useState(getUserRolesFromSession());

  const { isDev, selectedProduct } = useMemo(() => {
    const host = window.location.hostname;

    switch (host) {
      case hostnames.cmProd:
      case hostnames.cmOffersProd:
      case hostnames.cmAllocationProd:
      case hostnames.cmCPMProd:
        return { isDev: false, selectedProduct: EProduct.channels };
      case hostnames.cmStaging:
      case hostnames.cmOffersStaging:
      case hostnames.cmAllocationStaging:
      case hostnames.cmCPMStaging:
      case hostnames.cmSandbox:
      case hostnames.cmOffersSandbox:
      case hostnames.cmAllocationSandbox:
      case hostnames.cmCPMSandbox:
        return { isDev: true, selectedProduct: EProduct.channels };
      case hostnames.ibProd:
        return { isDev: false, selectedProduct: EProduct.inbound };
      case hostnames.ibSandbox:
        return { isDev: true, selectedProduct: EProduct.inbound };
      case hostnames.stProd:
      case hostnames.bbProd:
        return { isDev: false, selectedProduct: EProduct.buyback };
      case hostnames.stSandbox:
      case hostnames.bbSandbox:
        return { isDev: true, selectedProduct: EProduct.buyback };
      case hostnames.ivProd:
        return { isDev: false, selectedProduct: EProduct.inventory };
      case hostnames.ivSandbox:
        return { isDev: true, selectedProduct: EProduct.inventory };
      case hostnames.prProd:
        return { isDev: false, selectedProduct: EProduct.inventory };
      case hostnames.prSandbox:
        return { isDev: true, selectedProduct: EProduct.inventory };
      case hostnames.opProd:
        return { isDev: false, selectedProduct: EProduct.operations };
      case hostnames.opSandbox:
        return { isDev: true, selectedProduct: EProduct.operations };
      default:
        return { isDev: true };
    }
  }, []);

  const userProducts = useMemo(
    () =>
      productsConfig.reduce((result: IProductConfig[], product) => {
        const userModules = product.modules.filter(module => {
          const hasRole = module.roles?.some(role => userRoles.includes(role));

          if (!hasRole) return false;

          if (module.customRoles) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const userCustomRole = (getUserProfileFromSession() as any)?.[module.customRoles.name];

            return (
              (module.customRoles.allowNull && !userCustomRole) ||
              module.customRoles.roles.includes(userCustomRole)
            );
          }

          return true;
        });

        return userModules.length ? [...result, { ...product, modules: userModules }] : result;
      }, []),
    [userRoles],
  );

  useEffect(() => {
    const handler = (ev: CustomEvent<{ roles: string[] }>) => {
      setUserRoles(ev.detail.roles);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    document.addEventListener<any>('USER_LOGGED', handler);

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      document.removeEventListener<any>('USER_LOGGED', handler);
    };
  }, []);

  const handleLogout = () => {
    document.dispatchEvent(new CustomEvent('USER_LOGOUT_REQUEST'));
    document.dispatchEvent(new Event('LUX_CORE_LOGOUT_TRIGGER'));
  };

  return (
    <BasicSetup>
      <NavWrapper open={open}>
        <Nav>
          <LuxLogo open={open} />
          <div>
            <IconButton className="navigation-menu-btn" onClick={() => setOpen(!open)}>
              {open ? (
                <MaterialIcons.ArrowBackIosNewOutlined fontSize="small" color="secondary" />
              ) : (
                <MaterialIcons.ArrowForwardIosOutlined fontSize="small" color="secondary" />
              )}
            </IconButton>
          </div>
          <Routes>
            <Route
              path="*"
              element={
                <StyledLinks open={open}>
                  {userProducts.map(
                    ({ baseUrlProd, baseUrlSandbox, baseUrlStaging, product, ...props }) => (
                      <NavItem
                        {...props}
                        baseUrl={
                          selectedProduct &&
                          (isDev
                            ? window.IS_STAGING && baseUrlStaging
                              ? baseUrlStaging
                              : baseUrlSandbox
                            : baseUrlProd)
                        }
                        key={props.title}
                        open={open}
                        selected={selectedProduct === product}
                      />
                    ),
                  )}
                </StyledLinks>
              }
            ></Route>
          </Routes>
          <StyledBottomButton open={open}>
            <SquaredIconButton
              className="small-icon-button"
              size="small"
              sx={{ border: 'none' }}
              onClick={handleLogout}
            >
              <MaterialIcons.Logout fontSize="small" />
            </SquaredIconButton>
            <Button
              className="full-size-button"
              size="small"
              startIcon={<MaterialIcons.Logout fontSize="small" />}
              onClick={handleLogout}
            >
              {open && 'Logout'}
            </Button>
          </StyledBottomButton>
        </Nav>
      </NavWrapper>
    </BasicSetup>
  );
};

export default Navigation;
